import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where, 
  orderBy,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot
} from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { 
  FaShoppingCart, 
  FaTshirt, 
  FaTag, 
  FaPlus, 
  FaMinus, 
  FaTrash,
  FaCreditCard,
  FaGift,
  FaStore,
  FaCheck,
  FaStar,
  FaHeart,
  FaSearch,
  FaFilter,
  FaShippingFast
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import TopBar from '../components/Layout/TopBar';

const Boutique = () => {
  const [user] = useAuthState(auth);
  const [produits, setProduits] = useState([]);
  const [panier, setPanier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [commandeValidee, setCommandeValidee] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [favoris, setFavoris] = useState([]);

  const categories = [
    { id: 'all', name: 'Tous les produits', icon: FaStore },
    { id: 'maillot', name: 'Maillots & Textiles', icon: FaTshirt },
    { id: 'accessoire', name: 'Accessoires', icon: FaGift },
    { id: 'supporter', name: 'Articles Supporters', icon: FaTag }
  ];

  useEffect(() => {
    loadProduits();
    if (user) {
      loadPanier();
      loadFavoris();
    }
  }, [user]);

  // Chargement des produits depuis Firestore  
  const loadProduits = async () => {
    try {
      setLoading(true);
      
      // Solution simple : charger tous les produits et filtrer côté client
      const snapshot = await getDocs(collection(db, 'boutique_produits'));
      
      const allProduits = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dateCreation: data.dateCreation?.toDate(),
          dateModification: data.dateModification?.toDate()
        };
      });
      
      // Filtrer et trier côté client
      const produitsActifs = allProduits
        .filter(p => p.actif !== false) // Afficher les produits actifs ou sans statut défini
        .sort((a, b) => {
          const dateA = a.dateCreation || new Date(0);
          const dateB = b.dateCreation || new Date(0);
          return dateB - dateA;
        });
      
      setProduits(produitsActifs);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur lors du chargement des produits:', error);
      }
      setProduits([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPanier = async () => {
    if (!user) return;
    
    try {
      const q = query(
        collection(db, 'panier'),
        where('userId', '==', user.uid),
        where('commande', '==', false)
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const panierData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          dateCreation: doc.data().dateCreation?.toDate()
        }));
        setPanier(panierData);
      });

      return unsubscribe;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur loadPanier:', error);
      }
    }
  };

  const loadFavoris = async () => {
    if (!user) return;
    
    try {
      const q = query(
        collection(db, 'favoris'),
        where('userId', '==', user.uid)
      );
      
      const snapshot = await getDocs(q);
      const favorisData = snapshot.docs.map(doc => doc.data().produitId);
      setFavoris(favorisData);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur loadFavoris:', error);
      }
    }
  };

  const ajouterAuPanier = async (produit, taille = 'M', quantite = 1) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour ajouter des articles au panier', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return;
    }

    try {
      // Vérifier si l'article existe déjà dans le panier
      const existingItem = panier.find(
        item => item.produitId === produit.id && item.taille === taille
      );

      if (existingItem) {
        // Mettre à jour la quantité
        await updateDoc(doc(db, 'panier', existingItem.id), {
          quantite: existingItem.quantite + quantite,
          dateModification: new Date()
        });
      } else {
        // Ajouter un nouvel article
        await addDoc(collection(db, 'panier'), {
          userId: user.uid,
          produitId: produit.id,
          nom: produit.nom,
          prix: produit.prix,
          image: produit.images?.[0] || '',
          taille: taille,
          quantite: quantite,
          commande: false,
          dateCreation: new Date()
        });
      }
      
      setSelectedProduct(null);
      toast.success('Produit ajouté au panier !', {
        icon: '🛒',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur ajouterAuPanier:', error);
      }
      toast.error('Erreur lors de l\'ajout au panier', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const modifierQuantite = async (itemId, nouvelleQuantite) => {
    if (nouvelleQuantite <= 0) {
      supprimerDuPanier(itemId);
      return;
    }

    try {
      await updateDoc(doc(db, 'panier', itemId), {
        quantite: nouvelleQuantite,
        dateModification: new Date()
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur modifierQuantite:', error);
      }
      toast.error('Erreur lors de la modification', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const supprimerDuPanier = async (itemId) => {
    try {
      await deleteDoc(doc(db, 'panier', itemId));
      toast.success('Article supprimé du panier', {
        icon: '🗑️',
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur supprimerDuPanier:', error);
      }
      toast.error('Erreur lors de la suppression', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const toggleFavori = async (produitId) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour gérer vos favoris', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
      return;
    }

    try {
      const isFavori = favoris.includes(produitId);
      
      if (isFavori) {
        // Supprimer des favoris
        const q = query(
          collection(db, 'favoris'),
          where('userId', '==', user.uid),
          where('produitId', '==', produitId)
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(async (document) => {
          await deleteDoc(doc(db, 'favoris', document.id));
        });
        setFavoris(prev => prev.filter(id => id !== produitId));
      } else {
        // Ajouter aux favoris
        await addDoc(collection(db, 'favoris'), {
          userId: user.uid,
          produitId: produitId,
          dateCreation: new Date()
        });
        setFavoris(prev => [...prev, produitId]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur toggleFavori:', error);
      }
      toast.error('Erreur lors de la gestion des favoris', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const calculerTotal = () => {
    return panier.reduce((total, item) => total + (item.prix * item.quantite), 0);
  };

  const passerCommande = async (methodePaiement) => {
    if (!user || panier.length === 0) return;

    try {
      // Créer la commande
      const commandeData = {
        userId: user.uid,
        userEmail: user.email,
        userNom: user.displayName || user.email,
        articles: panier.map(item => ({
          produitId: item.produitId,
          nom: item.nom,
          prix: item.prix,
          quantite: item.quantite,
          taille: item.taille,
          image: item.image
        })),
        total: calculerTotal(),
        statut: 'en_attente',
        methodePaiement: methodePaiement,
        dateCommande: new Date(),
        adresseLivraison: {
          // Ici vous pourriez récupérer l'adresse de l'utilisateur
          nom: user.displayName || user.email,
          email: user.email
        }
      };

      await addDoc(collection(db, 'boutique_commandes'), commandeData);

      // Marquer les articles du panier comme commandés
      const updatePromises = panier.map(item =>
        updateDoc(doc(db, 'panier', item.id), { commande: true })
      );
      await Promise.all(updatePromises);

      // Mettre à jour le stock des produits
      const stockPromises = panier.map(async (item) => {
        const produit = produits.find(p => p.id === item.produitId);
        if (produit && produit.stock >= item.quantite) {
          await updateDoc(doc(db, 'boutique_produits', item.produitId), {
            stock: produit.stock - item.quantite,
            ventes: (produit.ventes || 0) + item.quantite
          });
        }
      });
      await Promise.all(stockPromises);

      setPanier([]);
      setShowCheckout(false);
      setCommandeValidee(true);
      
      // Recharger les produits pour mettre à jour le stock
      loadProduits();
      
      setTimeout(() => {
        setCommandeValidee(false);
        setShowCart(false);
      }, 3000);

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Erreur passerCommande:', error);
      }
      toast.error('Erreur lors de la commande. Veuillez réessayer.', {
        style: {
          background: '#001B56',
          color: '#FFE400',
        }
      });
    }
  };

  const filtrerProduits = () => {
    let produitsFiltres = [...produits];

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      produitsFiltres = produitsFiltres.filter(p => p.categorie === categoryFilter);
    }

    // Filtre par recherche
    if (searchTerm) {
      produitsFiltres = produitsFiltres.filter(p =>
        p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Tri
    switch (sortBy) {
      case 'price_asc':
        produitsFiltres.sort((a, b) => a.prix - b.prix);
        break;
      case 'price_desc':
        produitsFiltres.sort((a, b) => b.prix - a.prix);
        break;
      case 'name':
        produitsFiltres.sort((a, b) => a.nom.localeCompare(b.nom));
        break;
      case 'rating':
        produitsFiltres.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default: // recent
        produitsFiltres.sort((a, b) => 
          new Date(b.dateCreation || 0) - new Date(a.dateCreation || 0)
        );
    }

    return produitsFiltres;
  };

  const produitsAffiches = filtrerProduits();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#001B56] via-[#002a7a] to-[#001B56]">
      <TopBar />
      <div className="sticky top-4 z-50 flex flex-col items-center w-full pt-10">
        <div className="relative w-[90vw] max-w-2xl">
          <Navbar />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header avec effet glassmorphism */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="backdrop-blur-md bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] rounded-2xl shadow-lg">
                  <FaStore className="text-3xl text-[#001B56]" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-white mb-2">
                    Boutique Officielle EFAFC
                  </h1>
                  <p className="text-blue-200 text-lg">
                    Montrez vos couleurs avec fierté ! 💙💛
                  </p>
                </div>
              </div>
              
              {user && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowCart(true)}
                  className="relative flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl hover:shadow-xl transition-all duration-300 font-bold text-lg"
                >
                  <FaShoppingCart className="text-xl" />
                  <span>Mon Panier</span>
                  {panier.length > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg"
                    >
                      {panier.reduce((total, item) => total + item.quantite, 0)}
                    </motion.span>
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Barre de recherche et filtres */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" />
              <input
                type="text"
                placeholder="Rechercher un produit..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-[#FFE400] focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-[#FFE400]"
              >
                <option value="recent">Plus récents</option>
                <option value="price_asc">Prix croissant</option>
                <option value="price_desc">Prix décroissant</option>
                <option value="rating">Mieux notés</option>
                <option value="name">Nom A-Z</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Filtres par catégorie */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex gap-4 overflow-x-auto pb-2">
            {categories.map((category, index) => {
              const IconComponent = category.icon;
              return (
                <motion.button
                  key={category.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCategoryFilter(category.id)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-2xl whitespace-nowrap transition-all duration-300 backdrop-blur-md border ${
                    categoryFilter === category.id
                      ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] border-[#FFE400] shadow-xl'
                      : 'bg-white/10 text-white border-white/20 hover:bg-white/20'
                  }`}
                >
                  <IconComponent className="text-xl" />
                  <div className="text-left">
                    <div className="font-semibold">{category.name}</div>
                    <div className="text-sm opacity-80">{produits.filter(p => p.categorie === category.id).length} produits</div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Liste des produits */}
        {loading ? (
          <div className="text-center py-16">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="inline-block rounded-full h-16 w-16 border-4 border-[#FFE400] border-t-transparent mx-auto mb-4"
            />
            <p className="text-xl text-blue-200">Chargement de la boutique...</p>
          </div>
        ) : produitsAffiches.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 backdrop-blur-md bg-white/10 rounded-3xl border border-white/20"
          >
            <FaStore className="mx-auto text-8xl text-blue-300 mb-6" />
            <h3 className="text-2xl font-bold text-white mb-3">Aucun produit trouvé</h3>
            <p className="text-blue-200 text-lg">Essayez de modifier vos critères de recherche</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {produitsAffiches.map((produit, index) => (
              <motion.div
                key={produit.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="backdrop-blur-md bg-white/10 rounded-3xl border border-white/20 overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500">
                  {/* Image du produit */}
                  <div className="aspect-square bg-gradient-to-br from-gray-200 to-gray-300 relative overflow-hidden">
                    {produit.images?.[0] ? (
                      <img 
                        src={produit.images[0]} 
                        alt={produit.nom}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FFE400]/20 to-[#BDFF00]/20">
                        <FaTshirt className="text-8xl text-[#001B56]/30" />
                      </div>
                    )}
                    
                    {/* Badges */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                      {produit.nouveaute && (
                        <span className="px-3 py-1 bg-gradient-to-r from-[#BDFF00] to-[#FFE400] text-[#001B56] text-xs font-bold rounded-full">
                          NOUVEAU
                        </span>
                      )}
                      {produit.promotion && (
                        <span className="px-3 py-1 bg-rose-500/90 text-white text-xs font-bold rounded-full">
                          -{produit.promotion}%
                        </span>
                      )}
                    </div>

                    {/* Bouton favori */}
                    <button 
                      onClick={() => toggleFavori(produit.id)}
                      className={`absolute top-4 right-4 p-2 z-10 backdrop-blur-sm rounded-full transition-colors ${
                        favoris.includes(produit.id)
                          ? 'bg-red-500 text-white'
                          : 'bg-black/20 text-white hover:bg-red-500'
                      }`}
                    >
                      <FaHeart />
                    </button>

                    {/* Aperçu rapide */}
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={() => setSelectedProduct(produit)}
                        className="px-6 py-3 bg-white text-[#001B56] rounded-xl font-bold hover:bg-[#FFE400] transition-colors"
                      >
                        Aperçu rapide
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-lg text-white leading-tight">{produit.nom}</h3>
                      {produit.rating && (
                        <div className="flex items-center gap-1">
                          <FaStar className="text-[#FFE400] text-sm" />
                          <span className="text-white text-sm font-semibold">{produit.rating}</span>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-blue-200 text-sm mb-4 line-clamp-2">{produit.description}</p>
                    
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {produit.promotion && produit.prixOriginal ? (
                          <>
                            <span className="text-xl font-bold text-[#FFE400]">
                              {produit.prix}€
                            </span>
                            <span className="text-sm text-gray-400 line-through">
                              {produit.prixOriginal}€
                            </span>
                          </>
                        ) : (
                          <span className="text-xl font-bold text-[#FFE400]">
                            {produit.prix}€
                          </span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        produit.stock > 10 
                          ? 'bg-[#BDFF00]/15 text-[#BDFF00]' 
                          : produit.stock > 0 
                            ? 'bg-[#FFE400]/15 text-[#FFE400]' 
                            : 'bg-rose-500/15 text-rose-400'
                      }`}>
                        {produit.stock > 0 ? `${produit.stock} en stock` : 'Rupture'}
                      </span>
                    </div>

                    {produit.tailles && produit.tailles.length > 0 && (
                      <div className="mb-4">
                        <p className="text-sm text-blue-200 mb-2">Tailles:</p>
                        <div className="flex gap-1 flex-wrap">
                          {produit.tailles.map(taille => (
                            <span 
                              key={taille}
                              className="px-2 py-1 bg-white/10 text-white text-xs rounded border border-white/15"
                            >
                              {taille}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => ajouterAuPanier(produit)}
                      disabled={produit.stock <= 0}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-xl hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed transition-all duration-300 font-bold"
                    >
                      <FaPlus />
                      {produit.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Aperçu Produit */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedProduct(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Image */}
                <div className="aspect-square bg-gray-200 relative">
                  {selectedProduct.images?.[0] ? (
                    <img 
                      src={selectedProduct.images[0]} 
                      alt={selectedProduct.nom}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FaTshirt className="text-8xl text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Détails */}
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <h2 className="text-2xl font-bold text-[#001B56]">{selectedProduct.nom}</h2>
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                  
                  {selectedProduct.rating && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <FaStar 
                            key={i} 
                            className={`text-sm ${i < Math.floor(selectedProduct.rating) ? 'text-[#FFE400]' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <span className="text-[#001B56] font-semibold">{selectedProduct.rating}</span>
                      <span className="text-gray-500">({selectedProduct.reviews} avis)</span>
                    </div>
                  )}
                  
                  <p className="text-gray-600 mb-6">{selectedProduct.description}</p>
                  
                  <div className="flex items-center gap-3 mb-6">
                    {selectedProduct.promotion && selectedProduct.prixOriginal ? (
                      <>
                        <span className="text-3xl font-bold text-[#001B56]">
                          {selectedProduct.prix}€
                        </span>
                        <span className="text-lg text-gray-500 line-through">
                          {selectedProduct.prixOriginal}€
                        </span>
                        <span className="px-2 py-1 bg-red-100 text-red-600 text-sm font-bold rounded">
                          -{selectedProduct.promotion}%
                        </span>
                      </>
                    ) : (
                      <span className="text-3xl font-bold text-[#001B56]">
                        {selectedProduct.prix}€
                      </span>
                    )}
                  </div>

                  {selectedProduct.tailles && selectedProduct.tailles.length > 0 && (
                    <div className="mb-6">
                      <p className="font-semibold mb-3">Tailles disponibles:</p>
                      <div className="flex gap-2">
                        {selectedProduct.tailles.map(taille => (
                          <button
                            key={taille}
                            className="px-4 py-2 border-2 border-[#001B56] text-[#001B56] rounded-lg hover:bg-[#001B56] hover:text-white transition-colors"
                          >
                            {taille}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-4 mb-6">
                    <FaShippingFast className="text-green-600 text-xl" />
                    <span className="text-green-600 font-semibold">Livraison gratuite dès 50€</span>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => ajouterAuPanier(selectedProduct)}
                    disabled={selectedProduct.stock <= 0}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-xl hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed transition-all duration-300 font-bold text-lg"
                  >
                    <FaPlus />
                    {selectedProduct.stock > 0 ? 'Ajouter au panier' : 'Rupture de stock'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Panier */}
      <AnimatePresence>
        {showCart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, x: 100 }}
              animate={{ scale: 1, x: 0 }}
              exit={{ scale: 0.9, x: 100 }}
              className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-3xl font-bold text-[#001B56] flex items-center gap-3">
                    <FaShoppingCart />
                    Mon Panier
                  </h2>
                  <button
                    onClick={() => setShowCart(false)}
                    className="p-3 text-gray-400 hover:text-gray-600 transition-colors rounded-xl hover:bg-gray-100"
                  >
                    ✕
                  </button>
                </div>

                {panier.length === 0 ? (
                  <div className="text-center py-12">
                    <FaShoppingCart className="mx-auto text-8xl text-gray-300 mb-6" />
                    <h3 className="text-2xl font-bold text-gray-600 mb-3">Panier vide</h3>
                    <p className="text-gray-500 text-lg">Découvrez nos produits et ajoutez-les à votre panier !</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-6 mb-8">
                      {panier.map((item, index) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center gap-4 p-4 border border-gray-200 rounded-2xl hover:shadow-lg transition-shadow"
                        >
                          <div className="w-20 h-20 bg-gray-200 rounded-xl overflow-hidden">
                            <img 
                              src={item.image || '/api/placeholder/80/80'} 
                              alt={item.nom}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-[#001B56] text-lg">{item.nom}</h4>
                            {item.taille && (
                              <p className="text-sm text-gray-600">Taille: {item.taille}</p>
                            )}
                            <p className="text-[#FFE400] font-bold text-lg">{item.prix}€</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => modifierQuantite(item.id, item.quantite - 1)}
                              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <FaMinus />
                            </button>
                            <span className="font-bold text-lg min-w-[30px] text-center">{item.quantite}</span>
                            <button
                              onClick={() => modifierQuantite(item.id, item.quantite + 1)}
                              className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <FaPlus />
                            </button>
                            <button
                              onClick={() => supprimerDuPanier(item.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-3"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <div className="flex justify-between items-center mb-6">
                        <span className="text-2xl font-bold text-[#001B56]">Total:</span>
                        <span className="text-3xl font-bold text-[#FFE400]">{calculerTotal()}€</span>
                      </div>
                      
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowCheckout(true)}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl hover:shadow-xl transition-all duration-300 font-bold"
                      >
                        <FaCreditCard />
                        Passer commande
                      </motion.button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Checkout */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-3xl max-w-lg w-full shadow-2xl"
            >
              <div className="p-8">
                <h2 className="text-3xl font-bold text-[#001B56] mb-8 flex items-center gap-3">
                  <FaCreditCard />
                  Finaliser la commande
                </h2>

                <div className="bg-gradient-to-r from-[#FFE400]/10 to-[#BDFF00]/10 rounded-2xl p-6 mb-8 border border-[#FFE400]/20">
                  <h3 className="font-bold text-[#001B56] mb-4 text-lg">Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    {panier.map(item => (
                      <div key={item.id} className="flex justify-between">
                        <span className="text-gray-700">{item.nom} (x{item.quantite})</span>
                        <span className="font-semibold text-[#001B56]">{item.prix * item.quantite}€</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-[#FFE400]/30 mt-4 pt-4 font-bold flex justify-between text-lg">
                    <span className="text-[#001B56]">Total:</span>
                    <span className="text-[#FFE400]">{calculerTotal()}€</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => passerCommande('carte')}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:shadow-xl transition-all duration-300 font-bold"
                  >
                    <FaCreditCard />
                    Payer par carte (Démo)
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => passerCommande('paypal')}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-2xl hover:shadow-xl transition-all duration-300 font-bold"
                  >
                    PayPal (Démo)
                  </motion.button>

                  <button
                    onClick={() => setShowCheckout(false)}
                    className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Annuler
                  </button>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-700 text-center">
                    💡 <strong>Mode démonstration</strong> - Aucun paiement réel ne sera effectué
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Commande validée */}
      <AnimatePresence>
        {commandeValidee && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <FaCheck className="text-white text-3xl" />
              </motion.div>
              
              <h2 className="text-3xl font-bold text-[#001B56] mb-4">Commande validée !</h2>
              <p className="text-gray-600 mb-2 text-lg">
                Votre commande a été prise en compte avec succès.
              </p>
              <p className="text-sm text-blue-600 mb-8">
                Vous recevrez un email de confirmation sous peu.
              </p>
              
              <div className="bg-gradient-to-r from-[#FFE400]/10 to-[#BDFF00]/10 rounded-xl p-4 mb-6">
                <p className="text-sm text-[#001B56] font-semibold">
                  🎯 Mode démonstration
                </p>
                <p className="text-xs text-gray-600">
                  Aucun paiement réel - Projet académique
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCommandeValidee(false)}
                className="px-8 py-3 bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl hover:shadow-xl transition-all duration-300 font-bold"
              >
                Continuer mes achats
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Boutique;